/** Countdown driven by a server deadline (never by a local start time, so a reconnect shows
 * the same remaining time as the room). Announces the last five seconds once; never the only
 * cue for closing, since the teacher can close early or extend. */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  /** ISO timestamp when acceptance closes; null means no timer. */
  deadline: string | null;
  size?: "control" | "projector";
  /** Server-side "now" offset in ms if the client clock is known to drift. */
  skewMs?: number;
};

export function useRemaining(deadline: string | null, skewMs = 0): number | null {
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!deadline) {
      setLeft(null);
      return;
    }
    const end = new Date(deadline).getTime();
    const tick = () => setLeft(Math.max(0, Math.round((end - (Date.now() + skewMs)) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [deadline, skewMs]);
  return left;
}

export function Timer({ deadline, size = "control", skewMs }: Props) {
  const { t } = useTranslation();
  const left = useRemaining(deadline, skewMs);
  if (left === null) return null;
  const ending = left <= 5 && left > 0;
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div
      className={[size === "projector" ? "text-6xl" : "text-lg", "font-semibold tabular-nums"].join(" ")}
      style={{ color: ending ? "var(--not-yet)" : left === 0 ? "var(--ink-2)" : "var(--ink)" }}
      role="timer"
      aria-live="off"
      aria-label={left === 0 ? t("timer.closed") : t("timer.remaining", { s: left })}
    >
      <span aria-hidden>⏱︎ </span>
      {mm}:{ss}
    </div>
  );
}
